/**
 * Google Analytics 4 — Boliche Nicaragua (G-380PE682FP)
 *
 * `window.gtag` lo define public/index.html apenas carga la página (cola en dataLayer), así
 * nada se pierde aunque el script de Google llegue después. No se mide:
 *  - localhost (desarrollo)
 *  - el admin (si hay admin_token en este navegador): sus visitas inflarían los números
 * Las páginas llevan un nombre legible (page_title) para leer los reportes por sección.
 */

import { visitaEvento, visitaPagina } from './visitas';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
    __gaActivo?: boolean;
  }
}

function activo(): boolean {
  if (typeof window.gtag !== 'function' || !window.__gaActivo) return false;
  try { if (localStorage.getItem('admin_token')) return false; } catch { /* sin storage */ }
  return true;
}

function trackEvent(eventName: string, params?: Record<string, any>) {
  visitaEvento(eventName, params);   // registro propio (pantalla Visitas del admin)
  if (!activo()) return;
  window.gtag('event', eventName, params || {});
}

// ═══════════════════════════════════════
// PÁGINAS
// ═══════════════════════════════════════

/** Nombre legible de cada sección para los reportes (y el título de la pestaña). */
const RUTAS: [RegExp, string][] = [
  [/^\/$/, 'Inicio'],
  [/^\/noticias\/.+/, 'Noticia'],
  [/^\/noticias/, 'Noticias'],
  [/^\/mercado\/publicar/, 'Mercadito · Publicar'],
  [/^\/mercado\/editar/, 'Mercadito · Editar'],
  [/^\/mercado\/mis-productos/, 'Mercadito · Mis productos'],
  [/^\/mercado\/.+/, 'Mercadito · Producto'],
  [/^\/mercado/, 'Mercadito'],
  [/^\/mi-torneo/, 'Mi torneo'],
  [/^\/mi-perfil/, 'Mi perfil'],
  [/^\/aprende/, 'Aprendé'],
  [/^\/canal/, 'Canal de WhatsApp'],
  [/^\/relampago/, 'Relámpago'],
  [/^\/apuestas/, 'Herraduras'],
];
export function nombreDePagina(path: string): string {
  const limpio = path.split('?')[0];
  return RUTAS.find(([re]) => re.test(limpio))?.[1] || 'Otra';
}

/** Una visita de página (SPA): se llama en cada cambio de ruta. */
export function trackPageView(path: string, title?: string) {
  visitaPagina(path, title || nombreDePagina(path));   // registro propio (pantalla Visitas del admin)
  if (!activo()) return;
  window.gtag('event', 'page_view', {
    page_location: window.location.origin + path,
    page_path: path,
    page_title: title || nombreDePagina(path),
  });
}

/** Jugador con sesión (PIN): se identifica con su id de cuenta (no es un dato personal) para
 *  contar usuarios reales entre celulares y ver cuántos usan la app con su cuenta. */
export function identificarJugador(jugadorId: string | null) {
  if (typeof window.gtag !== 'function' || !window.__gaActivo) return;
  window.gtag('set', { user_id: jugadorId || null });  // null = sin cuenta (se limpia al salir)
  window.gtag('set', 'user_properties', { tipo_usuario: jugadorId ? 'jugador_con_pin' : 'visitante' });
}

// ═══════════════════════════════════════
// EVENTOS
// ═══════════════════════════════════════

// ─── Cuentas de jugador ───
/** Ingreso con teléfono + PIN (evento recomendado de GA4). */
export function trackLoginJugador(method: 'pin' | 'faceid' = 'pin') {
  trackEvent('login', { method });
}
/** Pestaña de Mi torneo. */
export function trackMiTorneoTab(tab: string) {
  trackEvent('mi_torneo_tab', { tab_nombre: tab });
}

// ─── Noticias ───
export function trackVerNoticia(noticiaId: string, titulo: string) {
  trackEvent('ver_noticia', { noticia_id: noticiaId, titulo });
}
export function trackVerGaleria(noticiaId: string) {
  trackEvent('ver_galeria_fotos', { noticia_id: noticiaId });
}

// ─── Compartir (evento recomendado de GA4) ───
export function trackCompartir(tipo: 'noticia' | 'producto' | 'torneo', id: string, metodo: 'share' | 'copiar') {
  trackEvent('share', { content_type: tipo, item_id: id, method: metodo });
}

// ─── ¿Cuándo juega tu equipo? (portada) ───
export function trackCuandoJuega(como: 'boton' | 'texto', resultado: string) {
  trackEvent('cuando_juega', { method: como, resultado });
}
/** El texto de una pregunta escrita a la IA: queda solo en Visitas (nuestra base), NO va a Google
 *  Analytics (puede traer nombres). `lugar`: portada | mi_torneo | que_pasa_si. */
export function registrarPreguntaIA(lugar: 'portada' | 'mi_torneo' | 'que_pasa_si', pregunta: string, resultado: string) {
  visitaEvento('pregunta_ia', { lugar, pregunta: pregunta.slice(0, 200), resultado });
}
/** «Preguntale a la IA» de Mi torneo: respuesta o «¿quisiste decir…?». */
export function trackPreguntaleIA(resultado: 'respuesta' | 'duda') {
  trackEvent('preguntale_ia', { lugar: 'mi_torneo', resultado });
}
/** «¿Qué pasa si…?» de Mi torneo: escenario escrito (IA) o fijado a mano. */
export function trackQuePasaSi(como: 'texto' | 'manual', resultado: string) {
  trackEvent('que_pasa_si', { method: como, resultado });
}

// ─── Aviso del Relámpago (portada) ───
/** Tarjeta/popup del próximo Relámpago: vista del popup, «Anotarme», «¿Cómo se juega?» o cerrar. */
export function trackRelampagoAviso(accion: 'ver_popup' | 'anotarme' | 'como_se_juega' | 'cerrar', formato: string) {
  trackEvent('relampago_aviso', { accion, formato });
}

// ─── Encuestas ───
/** La encuesta: se vio, empezó a responder o la envió (y la tarjeta del inicio). */
export function trackEncuesta(accion: 'ver' | 'empezar' | 'enviar' | 'tarjeta_inicio', encuesta: string) {
  trackEvent('encuesta', { accion, encuesta });
}

// ─── Canal de WhatsApp ───
/** Clic en "Seguir el canal": el objetivo de la tarjeta de la portada y de /canal. */
export function trackSeguirCanal(lugar: 'portada' | 'pagina_canal' | 'aprende') {
  trackEvent('seguir_canal', { placement: lugar });
}

// ─── Mercadito ───
export function trackVerProducto(productoId: string, nombre: string, categoria: string) {
  trackEvent('ver_producto', { producto_id: productoId, producto_nombre: nombre, categoria });
}
export function trackContactarVendedor(productoId: string, categoria: string) {
  trackEvent('contactar_vendedor', { producto_id: productoId, categoria });
}
export function trackPublicarProducto(categoria: string, editando: boolean) {
  trackEvent(editando ? 'editar_producto' : 'publicar_producto', { categoria });
}
